import { getNearestDemandRollupTargetId } from "src/allocation/util/groupHierarchy.util";

const getMacroAllocationId = (
  allocationProjectId,
  sourceGroupId,
  targetGroupId
) =>
  `macro-allocation-${allocationProjectId}-${sourceGroupId}-${targetGroupId}`;

const getMacroAllocationKey = (macroAllocation, targetGroupId) =>
  [
    macroAllocation.allocationProjectId,
    macroAllocation.sourceGroupId,
    targetGroupId,
  ].join("|");

const numericRollupFields = [
  "requested",
  "requestSubmitted",
  "approved",
  "approvalSubmitted",
  "forecast",
  "allocatedMemberFte",
  "realtimeCurrentMemberFte",
];

const sumPresentValues = (existing, incoming, field) => {
  const left = existing[field];
  const right = incoming[field];
  if (left == null && right == null) {
    return undefined;
  }

  return (left || 0) + (right || 0);
};

const mergeMacroAllocations = (existing, incoming) => {
  const merged = { ...existing, ...incoming };

  numericRollupFields.forEach((field) => {
    const value = sumPresentValues(existing, incoming, field);
    if (value == null) {
      delete merged[field];
      return;
    }

    merged[field] = value;
  });

  return merged;
};

const mergeIntoRollupTargetAllocation = (rollupTargetAllocation, incoming) => {
  const merged = {
    ...rollupTargetAllocation,
    ...incoming,
  };
  const allocatedMemberFte =
    rollupTargetAllocation.allocatedMemberFte ?? incoming.allocatedMemberFte;
  const realtimeCurrentMemberFte =
    rollupTargetAllocation.realtimeCurrentMemberFte ??
    incoming.realtimeCurrentMemberFte;

  if (allocatedMemberFte == null) {
    delete merged.allocatedMemberFte;
  } else {
    merged.allocatedMemberFte = allocatedMemberFte;
  }

  if (realtimeCurrentMemberFte == null) {
    delete merged.realtimeCurrentMemberFte;
  } else {
    merged.realtimeCurrentMemberFte = realtimeCurrentMemberFte;
  }

  return merged;
};

const remapToDemandRollupTarget = ({
  macroAllocation,
  groupLookup,
  groupTypes,
}) => {
  const targetGroup = groupLookup[macroAllocation.targetGroupId];
  if (groupTypes[targetGroup?.type]?.planning?.isDemandRollupTarget) {
    return macroAllocation;
  }

  const demandRollupAncestorId = getNearestDemandRollupTargetId({
    group: targetGroup,
    groupLookup,
    groupTypes,
    includeSelf: false,
  });

  if (!demandRollupAncestorId) {
    return macroAllocation;
  }

  return {
    ...macroAllocation,
    id: getMacroAllocationId(
      macroAllocation.allocationProjectId,
      macroAllocation.sourceGroupId,
      demandRollupAncestorId
    ),
    targetGroupId: demandRollupAncestorId,
  };
};

const filterDemandRollupContextMacroAllocations = ({
  macroAllocations = [],
  groupLookup = {},
  groupTypes = {},
  enableDemandRollupPlanning = false,
}) => {
  if (!enableDemandRollupPlanning) {
    return macroAllocations;
  }

  const rollupTargetAllocations = [];
  const descendantAllocations = [];

  macroAllocations.forEach((macroAllocation) => {
    const targetGroup = groupLookup[macroAllocation.targetGroupId];
    if (groupTypes[targetGroup?.type]?.planning?.isDemandRollupTarget) {
      rollupTargetAllocations.push(macroAllocation);
      return;
    }

    descendantAllocations.push(macroAllocation);
  });

  const remappedDescendantAllocations = new Map();

  descendantAllocations.forEach((macroAllocation) => {
    const remappedMacroAllocation = remapToDemandRollupTarget({
      macroAllocation,
      groupLookup,
      groupTypes,
    });
    const remappedKey = getMacroAllocationKey(
      remappedMacroAllocation,
      remappedMacroAllocation.targetGroupId
    );

    const existing = remappedDescendantAllocations.get(remappedKey);
    remappedDescendantAllocations.set(
      remappedKey,
      existing
        ? mergeMacroAllocations(existing, remappedMacroAllocation)
        : remappedMacroAllocation
    );
  });

  const mergedRollupTargetAllocations = rollupTargetAllocations.map(
    (macroAllocation) => {
      const key = getMacroAllocationKey(
        macroAllocation,
        macroAllocation.targetGroupId
      );
      const remappedDescendantAllocation =
        remappedDescendantAllocations.get(key);
      if (!remappedDescendantAllocation) {
        return macroAllocation;
      }

      remappedDescendantAllocations.delete(key);
      return mergeIntoRollupTargetAllocation(
        macroAllocation,
        remappedDescendantAllocation
      );
    }
  );

  return [
    ...mergedRollupTargetAllocations,
    ...remappedDescendantAllocations.values(),
  ];
};

export default filterDemandRollupContextMacroAllocations;
